import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { deleteAzById } from '../../containers/settingsSlice';
import { AVAILABILITY_ZONE_FIELDS } from '../../common/fields';
import SelectField from '../Controls/SelectField/SelectField';
import Button from '../Controls/Button/Button';
import TextInput from '../Controls/TextInput/TextInput';
import * as actions from '../../containers/AvailabilityZones/availabilityZonesActions';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';
import { AZ_ENTITY_TYPES } from '../../containers/AvailabilityZones/availabilityZonesReducer';
import styles from './AvailabilityZone.module.css';
import { httpPost } from '../../../utils';

const buildOptions = (domains, unassigned, assigned, entitiesData) => {
    const filteredUnassigned = unassigned.filter((entity) => {
        if (entitiesData[entity] && entitiesData[entity].domain) {
            return domains.includes(entitiesData[entity].domain);
        }
        if (entitiesData[entity] && entitiesData[entity].domains) {
            // Checks if the domains are the same
            const domainsNoDuplications = new Set([...entitiesData[entity].domains, ...domains]);
            return (
                entitiesData[entity].domains.length === domainsNoDuplications.size &&
                domains.length === domainsNoDuplications.size
            );
        }
        return false;
    });

    const filteredAssigned = assigned.filter((entity) => entitiesData[entity]);
    return filteredUnassigned.concat(filteredAssigned).map((entity) => {
        return {
            label: entitiesData[entity].name,
            value: entitiesData[entity].id
        };
    });
};

const AvailabilityZone = ({
    azGuid,
    fields,
    dispatch,
    shared,
    dcsValue,
    sfNodesValue,
    protectedDomainsValue,
    allowDelete,
    disableDelete,
    setTabToZero = undefined,
    hideDcs = false
}) => {
    const storeDispatch = useDispatch();

    const [showDeleteMessage, setShowDeleteMessage] = useState(false);

    const name = fields[AVAILABILITY_ZONE_FIELDS.NAME.ID];
    const protectedDomains = fields[AVAILABILITY_ZONE_FIELDS.PROTECTED_DOMAINS.ID];
    const sfNodes = fields[AVAILABILITY_ZONE_FIELDS.SF_NODES.ID];
    const dcs = hideDcs ? false : fields[AVAILABILITY_ZONE_FIELDS.DCS.ID];
    const domainOptions = shared.domainOptions;

    const protectedDCs = (
        (fields[AVAILABILITY_ZONE_FIELDS.DCS.ID] && fields[AVAILABILITY_ZONE_FIELDS.DCS.ID].value) ||
        []
    ).filter((dcGuid) => shared.dcsWithDomains[dcGuid] && shared.dcsWithDomains[dcGuid].isProtected);

    const sfNodesOptions = useMemo(() => {
        return buildOptions(protectedDomainsValue, shared.unassigned.walls, sfNodesValue, shared.wallsWithDomains);
    }, [sfNodesValue, shared.unassigned.walls, shared.wallsWithDomains, protectedDomainsValue]);

    const dcsOptions = useMemo(() => {
        if (hideDcs) {
            return [];
        }
        return buildOptions(protectedDomainsValue, shared.unassigned.dcs, dcsValue, shared.dcsWithDomains);
    }, [hideDcs, dcsValue, shared.unassigned.dcs, shared.dcsWithDomains, protectedDomainsValue]);

    const dispatchUpdateEntities = useCallback(
        (fieldId, value) => {
            let type = AZ_ENTITY_TYPES.WALL;
            if (fieldId === AVAILABILITY_ZONE_FIELDS.DCS.ID) {
                type = AZ_ENTITY_TYPES.DC;
            }
            dispatch({
                type: actions.UPDATE_ENTITIES,
                payload: { azGuid, newValue: value, type }
            });
        },
        [dispatch, azGuid]
    );

    const dispatchRenameAz = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.RENAME_AZ,
                payload: { azGuid, newAzName: value }
            });
        },
        [dispatch, azGuid]
    );

    const dispatchChangeAzDomain = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.CHANGE_AZ_DOMAIN,
                payload: { azGuid, newDomain: value }
            });
        },
        [dispatch, azGuid]
    );

    const deleteAz = useCallback(
        async (callback) => {
            try {
                if (setTabToZero) setTabToZero();
                if (fields && fields.isNew && fields.isNew.value) {
                    dispatch({
                        type: actions.DELETE_AZ,
                        payload: { azGuid }
                    });
                    setShowDeleteMessage(false);
                } else {
                    const response = await httpPost(`/Settings/deleteAz`, {
                        azId: azGuid
                    });
                    if (response.data.result) {
                        dispatch({
                            type: actions.DELETE_AZ,
                            payload: { azGuid }
                        });
                        storeDispatch(
                            deleteAzById({
                                deletedId: azGuid,
                                newUnassignedDcs: response.data.unassignedDcs,
                                newUnassignedWalls: response.data.unassignedWalls
                            })
                        );
                        setShowDeleteMessage(false);
                    } else {
                        return callback(false, 'Failed to delete availability zone. Please try again later');
                    }
                }
            } catch (err) {
                return callback(false, 'Failed to delete availability zone. Please try again later');
            }
        },
        [azGuid, dispatch, storeDispatch, fields, setTabToZero]
    );
    const isNew = fields && fields.isNew && fields.isNew.value;
    return (
        <>
            {name && <TextInput field={name} changed={dispatchRenameAz} />}
            {protectedDomains && (
                <SelectField
                    field={protectedDomains}
                    changed={dispatchChangeAzDomain}
                    options={domainOptions}
                    creatable={false}
                    multichoice={true}
                />
            )}
            {sfNodes && (
                <SelectField
                    field={sfNodes}
                    changed={dispatchUpdateEntities}
                    options={sfNodesOptions}
                    creatable={false}
                    multichoice={true}
                />
            )}
            {dcs && (
                <SelectField
                    field={dcs}
                    changed={dispatchUpdateEntities}
                    options={dcsOptions}
                    creatable={false}
                    multichoice={true}
                />
            )}
            {(allowDelete || isNew) && (
                <div className={styles.marginTop}>
                    <Button
                        id={`az-${name?.value?.toLowerCase() ?? ''}-delete-availability-zone`}
                        onClick={() => setShowDeleteMessage(true)}
                        disabled={disableDelete && !isNew}
                        className={styles.deleteButton}
                        tooltipText={
                            disableDelete && !isNew ? 'Save all changes before deleting the Availability Zone' : null
                        }>
                        Delete Availability Zone
                    </Button>
                </div>
            )}

            {showDeleteMessage &&
                (protectedDCs && protectedDCs.length === 0 ? (
                    <ConfirmationMessage
                        title={'Delete Availability Zone'}
                        body={
                            <div>
                                {`Are you sure you want to delete ${
                                    name && name.value ? `"${name.value}"` : 'this availability zone'
                                } ?`}
                                <span>
                                    This will unassign all the nodes from this availability zone. You cannot undo this
                                    action.
                                </span>
                            </div>
                        }
                        approveText={'Delete'}
                        cancelText={'Cancel'}
                        approveStyle={'danger'}
                        OnCancel={() => setShowDeleteMessage(false)}
                        OnApprove={deleteAz}
                    />
                ) : (
                    <ConfirmationMessage
                        title={'Delete Availability Zone'}
                        body={
                            <div>
                                {`This Availability Zone cannot be deleted, since it has assigned DCs. To delete it, move its assigned DCs to another Availability Zone.`}
                            </div>
                        }
                        approveText={'OK'}
                        approveStyle={'primary'}
                        OnApprove={() => setShowDeleteMessage(false)}
                        OnHide={() => setShowDeleteMessage(false)}
                        hideCancelButton
                    />
                ))}
        </>
    );
};

export default React.memo(AvailabilityZone);
